import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation, useQuery, gql } from '@/apollo';
import { 
  Users, 
  Settings, 
  Server,
  CheckCircle,
  BarChart3,
  FileText,
  Cog,
  Mail,
  Bell,
  AlertTriangle,
  Shield,
  Upload,
  RefreshCw,
  Zap,
  Database,
  Trash2,
  Building,
  Bug,
  TestTube,
  Globe,
  BookOpen
} from 'lucide-react';

import { EmailConfigurationDashboard } from './EmailConfigurationDashboard';
import { UserManagement } from './UserManagement';
import { ApplicationSettings, NotesSettings, BulletinsSettings, SessionsSettings, GeolocationSettings } from './settings';
import { LogsManagement } from './LogsManagement';
import { BugReportFeedback } from './BugReportFeedback';
import { TestingDashboard } from './TestingDashboard';
import { PatchNotesPage } from './PatchNotesPage';
import { Notification, useNotification } from '../ui/Notification';
import { 
  GET_SYSTEM_HEALTH, 
  RESTART_SERVER
  // OPTIMIZE_DATABASE, // Reserved for future use
  // CLEAR_CACHE // Reserved for future use
} from '../../graphql/system-health';
import { GET_SYSTEM_INFO, GET_SYSTEM_STATS } from '../../graphql/system-info';

const formatBuildDate = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, '0');
  const timeZoneFormatter = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });
  const timeZone = timeZoneFormatter.formatToParts(date).find(part => part.type === 'timeZoneName')?.value || '';

  return `${month}/${day}/${year} ${displayHours}:${displayMinutes} ${ampm} ${timeZone}`;
};
import {
  IMPORT_INSURANCES,
  IMPORT_LOCATIONS,
  IMPORT_CONDITIONS,
  IMPORT_CUSTOMERS,
  IMPORT_APPOINTMENT_DETAILS,
  IMPORT_PROVIDERS
} from '../../graphql/imports';

// GraphQL query to check if SMTP is enabled
const GET_EMAIL_ENABLED = gql`
  query GetEmailEnabled {
    emailConfiguration {
      isEnabled
    }
  }
`;

import { formatSystemHealthData } from '../../utils/system-health-utils';
import { ServerRestartModal } from '../ui/ServerRestartModal';
import DatabaseBackupModal from '../ui/DatabaseBackupModal';
import { DatabaseOptimizeModal } from '../ui/DatabaseOptimizeModal';
import { ClearCacheModal } from '../ui/ClearCacheModal';
import { CSVUploadModal } from '../ui/CSVUploadModal';
import { CSVPreviewModal } from '../ui/CSVPreviewModal';
import { ApplicationSettingsModal } from '../ui/ApplicationSettingsModal';
import { Button } from '../ui/Button';
import { UPDATE_APPLICATION_SETTINGS, UPDATE_SESSION_SETTINGS, RESET_SESSION_SETTINGS, UPDATE_GEOLOCATION_SETTINGS } from '../../graphql/app-settings';
import { useApplicationSettings } from '../../contexts/ApplicationSettingsContext';
import { formatTimeInTimezone } from '../../utils/timezone-utils';
import { useApolloClient } from '@/apollo';
import { ConfirmModal } from '../../framework/modals/ConfirmModal';

interface AdminDashboardProps {}

export const AdminDashboard: React.FC<AdminDashboardProps> = () => {
  const client = useApolloClient();
  const [activeSection, setActiveSection] = useState('overview');
  const [activeLogTab, setActiveLogTab] = useState('logins');
  
  // Notification system
  const { notification, showNotification, hideNotification } = useNotification();
  
  // Add CSS for hiding number input spinners and scrollbar
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .no-spinners::-webkit-outer-spin-button,
      .no-spinners::-webkit-inner-spin-button {
        -webkit-appearance: none !important;
        margin: 0 !important;
      }
      .no-spinners {
        -moz-appearance: textfield !important;
      }
      .admin-dashboard-scrollbar::-webkit-scrollbar {
        display: none;
      }
      .admin-dashboard-scrollbar {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Get application settings from context
  const { settings, refreshSettings } = useApplicationSettings();
  
  // System Health Query
  const { data: systemHealthData, loading: systemHealthLoading, error: systemHealthError } = useQuery(GET_SYSTEM_HEALTH, {
    pollInterval: 5000, // Refresh every 5 seconds
    errorPolicy: 'all',
    fetchPolicy: 'network-only'
  });

  // System Info Query
  const { data: systemInfoData, loading: systemInfoLoading, error: systemInfoError } = useQuery(GET_SYSTEM_INFO, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-first'
  });

  // System Stats Query
  const { data: systemStatsData, loading: systemStatsLoading, error: systemStatsError } = useQuery(GET_SYSTEM_STATS, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-first'
  });

  // Email Configuration Query - Check if SMTP is enabled
  // Use network-only to ensure we get fresh data (important for production)
  const { data: emailConfigData, loading: emailConfigLoading, error: emailConfigError } = useQuery(GET_EMAIL_ENABLED, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only', // Always fetch fresh data to check current SMTP status
  });

  // Default to false if query is loading, has error, or data is missing
  // This ensures the bug report link is hidden until we confirm SMTP is enabled
  // Only show bug report if SMTP is explicitly enabled (strict check)
  // Check for both network errors and GraphQL errors (with errorPolicy: 'all', GraphQL errors are in data)
  const hasError = !!emailConfigError || (emailConfigData && 'errors' in emailConfigData);
  const isSmtpEnabled = !emailConfigLoading && 
                        !hasError && 
                        emailConfigData?.emailConfiguration?.isEnabled === true;

  // System Maintenance State
  const [isRestartModalOpen, setIsRestartModalOpen] = useState(false);
  const [restartInitiated, setRestartInitiated] = useState(false);
  const [isBackupModalOpen, setIsBackupModalOpen] = useState(false);
  const [isOptimizeModalOpen, setIsOptimizeModalOpen] = useState(false);
  const [isClearCacheModalOpen, setIsClearCacheModalOpen] = useState(false);
  const [isCSVUploadModalOpen, setIsCSVUploadModalOpen] = useState(false);
  const [isCSVPreviewModalOpen, setIsCSVPreviewModalOpen] = useState(false);
  const [csvImportType, setCsvImportType] = useState<'insurances' | 'locations' | 'conditions' | 'customers' | 'appointment-details' | 'providers'>('insurances');
  const [csvData, setCsvData] = useState<string>('');
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  
  // Application Settings State
  const [isAppSettingsModalOpen, setIsAppSettingsModalOpen] = useState(false);
  
  // General Settings State - Initialize from context with fallback values
  const [generalSettings, setGeneralSettings] = useState({
    applicationName: settings?.applicationName || 'Provider Selection Platform',
    companyName: settings?.companyName || 'Healthcare Solutions Inc.',
    supportEmail: settings?.supportEmail || 'support@healthcaresolutions.com',
    bugReportEmail: settings?.bugReportEmail || settings?.supportEmail || 'support@healthcaresolutions.com',
    defaultTimezone: settings?.defaultTimezone || 'America/New_York',
    notesEnabled: settings?.notesEnabled ?? true,
    bulletinsEnabled: settings?.bulletinsEnabled ?? true
  });

  // Session Settings State
  const [sessionSettings, setSessionSettings] = useState({
    sessionMaxDurationHours: settings?.sessionSettings?.sessionMaxDurationHours || 168,
    sessionCleanupIntervalHours: settings?.sessionSettings?.sessionCleanupIntervalHours || 1,
    maxConcurrentSessions: settings?.sessionSettings?.maxConcurrentSessions || 5,
    suspiciousActivityThreshold: settings?.sessionSettings?.suspiciousActivityThreshold || 50,
    sessionInactivityDays: settings?.sessionSettings?.sessionInactivityDays || 30,
    enableSessionAnalytics: settings?.sessionSettings?.enableSessionAnalytics ?? true,
    enableSessionFingerprinting: settings?.sessionSettings?.enableSessionFingerprinting ?? true,
    enableGeoTracking: settings?.sessionSettings?.enableGeoTracking ?? false,
  });

  // Session Settings State
  const [isSessionSettingsSaving, setIsSessionSettingsSaving] = useState(false);
  
  // Reset Confirmation Modals
  const [resetGeneralConfirm, setResetGeneralConfirm] = useState(false);
  const [resetSessionConfirm, setResetSessionConfirm] = useState(false);
  
  // Notes and Bulletins Settings State
  const [isNotesSettingsSaving, setIsNotesSettingsSaving] = useState(false);
  const [isBulletinsSettingsSaving, setIsBulletinsSettingsSaving] = useState(false);

  const [isGeneralSettingsSaving, setIsGeneralSettingsSaving] = useState(false);
  const [activeGeneralTab, setActiveGeneralTab] = useState<'application' | 'notes' | 'bulletins' | 'sessions' | 'geolocation'>('application');
  
  // Geolocation Settings State
  const [geolocationSettings, setGeolocationSettings] = useState({
    enabled: false,
    mode: 'ALLOW',
    countries: [] as string[],
    availableCountries: [] as Array<{ code: string; name: string }>,
  });
  const [isGeolocationSettingsSaving, setIsGeolocationSettingsSaving] = useState(false);
  const [activeImportTab, setActiveImportTab] = useState<'insurances' | 'locations' | 'conditions' | 'customers' | 'appointments' | 'providers'>('insurances');
  const [showFeatureToggleConfirm, setShowFeatureToggleConfirm] = useState(false);
  const [pendingFeatureToggle, setPendingFeatureToggle] = useState<{ feature: string; enabled: boolean } | null>(null);

  // Update local state when context settings change
  React.useEffect(() => {
    if (settings) {
      setGeneralSettings({
        applicationName: settings.applicationName || 'Provider Selection Platform',
        companyName: settings.companyName || 'Healthcare Solutions Inc.',
        supportEmail: settings.supportEmail || 'support@healthcaresolutions.com',
        bugReportEmail: settings.bugReportEmail || settings.supportEmail || 'support@healthcaresolutions.com',
        defaultTimezone: settings.defaultTimezone || 'America/New_York',
        notesEnabled: settings.notesEnabled ?? true,
        bulletinsEnabled: settings.bulletinsEnabled ?? true
      });

      setSessionSettings({
        sessionMaxDurationHours: settings.sessionSettings?.sessionMaxDurationHours || 168,
        sessionCleanupIntervalHours: settings.sessionSettings?.sessionCleanupIntervalHours || 1,
        maxConcurrentSessions: settings.sessionSettings?.maxConcurrentSessions || 5,
        suspiciousActivityThreshold: settings.sessionSettings?.suspiciousActivityThreshold || 50,
        sessionInactivityDays: settings.sessionSettings?.sessionInactivityDays || 30,
        enableSessionAnalytics: settings.sessionSettings?.enableSessionAnalytics ?? true,
        enableSessionFingerprinting: settings.sessionSettings?.enableSessionFingerprinting ?? true,
        enableGeoTracking: settings.sessionSettings?.enableGeoTracking ?? false,
      });

      if (settings.geolocationSettings) {
        setGeolocationSettings({
          enabled: settings.geolocationSettings.enabled || false,
          mode: settings.geolocationSettings.mode || 'ALLOW',
          countries: settings.geolocationSettings.countries || [],
          availableCountries: settings.geolocationSettings.availableCountries || [],
        });
      }
    }
  }, [settings]);



  // System Maintenance Mutations
  const [restartServer] = useMutation(RESTART_SERVER);
  // const [optimizeDatabase] = useMutation(OPTIMIZE_DATABASE); // Reserved for future use
  // const [clearCache] = useMutation(CLEAR_CACHE); // Reserved for future use
  
  // Application Settings Mutations
  const [updateApplicationSettings] = useMutation(UPDATE_APPLICATION_SETTINGS, {
    // Clear Apollo cache to ensure fresh settings are loaded everywhere
    update: (cache) => {
      cache.evict({ fieldName: 'applicationSettings' });
      cache.gc();
    }
  });

  // Session Settings Mutations
  const [updateSessionSettings] = useMutation(UPDATE_SESSION_SETTINGS, {
    // Clear Apollo cache to ensure fresh settings are loaded
    update: (cache) => {
      cache.evict({ fieldName: 'applicationSettings' });
      cache.evict({ fieldName: 'sessionSettings' });
      cache.gc();
    }
  });
  
  const [resetSessionSettings] = useMutation(RESET_SESSION_SETTINGS, {
    // Clear Apollo cache to ensure fresh settings are loaded
    update: (cache) => {
      cache.evict({ fieldName: 'applicationSettings' });
      cache.evict({ fieldName: 'sessionSettings' });
      cache.gc();
    }
  });

  // Geolocation Settings Mutations
  const [updateGeolocationSettings] = useMutation(UPDATE_GEOLOCATION_SETTINGS, {
    // Clear Apollo cache to ensure fresh settings are loaded
    update: (cache) => {
      cache.evict({ fieldName: 'applicationSettings' });
      cache.gc();
    }
  });
  
  // General Settings Handlers
  const handleGeneralSettingsChange = (field: string, value: any) => {
    // For feature toggles, show confirmation dialog
    if ((field === 'notesEnabled' || field === 'bulletinsEnabled') && typeof value === 'boolean') {
      const featureName = field === 'notesEnabled' ? 'Notes' : 'Bulletins';
      
      setPendingFeatureToggle({ feature: featureName, enabled: value });
      setShowFeatureToggleConfirm(true);
      return;
    }
    
    // For other fields, update immediately
    setGeneralSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const confirmFeatureToggle = () => {
    if (pendingFeatureToggle) {
      const field = pendingFeatureToggle.feature === 'Notes' ? 'notesEnabled' : 'bulletinsEnabled';
      setGeneralSettings(prev => ({
        ...prev,
        [field]: pendingFeatureToggle.enabled
      }));
      
      setShowFeatureToggleConfirm(false);
      setPendingFeatureToggle(null);
    }
  };

  const cancelFeatureToggle = () => {
    setShowFeatureToggleConfirm(false);
    setPendingFeatureToggle(null);
  };

  const handleSaveGeneralSettings = async () => {
    setIsGeneralSettingsSaving(true);
    try {
      // Ensure all required fields are present with fallback values
      const input = {
        applicationName: generalSettings.applicationName || 'Provider Selection Platform',
        companyName: generalSettings.companyName || 'Healthcare Solutions Inc.',
        supportEmail: generalSettings.supportEmail || 'support@healthcaresolutions.com',
        bugReportEmail: generalSettings.bugReportEmail || generalSettings.supportEmail || 'support@healthcaresolutions.com',
        defaultTimezone: generalSettings.defaultTimezone || 'America/New_York',
        notesEnabled: generalSettings.notesEnabled ?? true,
        bulletinsEnabled: generalSettings.bulletinsEnabled ?? true
      };
      
      console.log('Saving settings with input:', input); // Debug log
      
      await updateApplicationSettings({
        variables: { input }
      });
      
      // Refresh the context settings so they're available throughout the app
      await refreshSettings();
      
      setIsAppSettingsModalOpen(true); // Show success modal instead of popup
    } catch (error) {
      console.error('Failed to save general settings:', error);

    } finally {
      setIsGeneralSettingsSaving(false);
    }
  };

  const handleResetGeneralSettings = async () => {
    setResetGeneralConfirm(true);
  };

  const confirmResetGeneralSettings = async () => {
    try {
        // Reset to default values
        const defaultSettings = {
          applicationName: 'Provider Selection Platform',
          companyName: 'Healthcare Solutions Inc.',
          supportEmail: 'support@healthcaresolutions.com',
          bugReportEmail: 'support@healthcaresolutions.com',
          defaultTimezone: 'America/New_York',
          notesEnabled: true,
          bulletinsEnabled: true
        };
        
        await updateApplicationSettings({
          variables: { 
            input: defaultSettings
          }
        });
        
        // Update local state
        setGeneralSettings(defaultSettings);
        
        // Refresh the context settings
        await refreshSettings();
        setResetGeneralConfirm(false);
        showNotification('success', 'Settings Reset', 'General settings have been reset to defaults.');

      } catch (error) {
        console.error('Failed to reset general settings:', error);
        showNotification('error', 'Reset Failed', 'Failed to reset general settings. Please try again.');
      }
  };

  // Session Settings Handlers
  const handleSessionSettingsChange = (field: string, value: any) => {
    // Simply update local state - no auto-save
    setSessionSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveSessionSettings = async (e?: React.MouseEvent) => {
    // Prevent form submission if called from a button inside a form
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setIsSessionSettingsSaving(true);
    try {
      // Ensure all values are the correct types (Int for numbers, Boolean for booleans)
      const sanitizedInput = {
        sessionMaxDurationHours: parseInt(String(sessionSettings.sessionMaxDurationHours)) || 12,
        sessionCleanupIntervalHours: parseInt(String(sessionSettings.sessionCleanupIntervalHours)) || 1,
        maxConcurrentSessions: parseInt(String(sessionSettings.maxConcurrentSessions)) || 5,
        suspiciousActivityThreshold: parseInt(String(sessionSettings.suspiciousActivityThreshold)) || 50,
        sessionInactivityDays: parseInt(String(sessionSettings.sessionInactivityDays)) || 30,
        enableSessionAnalytics: Boolean(sessionSettings.enableSessionAnalytics),
        enableSessionFingerprinting: Boolean(sessionSettings.enableSessionFingerprinting),
        enableGeoTracking: Boolean(sessionSettings.enableGeoTracking),
      };
      
      console.log('[SESSION SETTINGS] Attempting to save:', sanitizedInput);
      console.log('[SESSION SETTINGS] Original settings:', sessionSettings);
      
      const result = await updateSessionSettings({
        variables: { input: sanitizedInput },
        errorPolicy: 'all' // Return both data and errors
      });
      
      console.log('[SESSION SETTINGS] Mutation result:', result);
      console.log('[SESSION SETTINGS] Result data:', result.data);
      console.log('[SESSION SETTINGS] Result errors:', result.errors);
      
      // Check for errors first
      if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
        throw new Error(result.errors[0]?.message || 'GraphQL error occurred');
      }
      
      if (!result.data?.updateSessionSettings) {
        throw new Error('No data returned from mutation');
      }
      
      if (result.data.updateSessionSettings) {
        // Update local state with the returned values
        setSessionSettings({
          sessionMaxDurationHours: result.data.updateSessionSettings.sessionMaxDurationHours,
          sessionCleanupIntervalHours: result.data.updateSessionSettings.sessionCleanupIntervalHours,
          maxConcurrentSessions: result.data.updateSessionSettings.maxConcurrentSessions,
          suspiciousActivityThreshold: result.data.updateSessionSettings.suspiciousActivityThreshold,
          sessionInactivityDays: result.data.updateSessionSettings.sessionInactivityDays,
          enableSessionAnalytics: result.data.updateSessionSettings.enableSessionAnalytics,
          enableSessionFingerprinting: result.data.updateSessionSettings.enableSessionFingerprinting,
          enableGeoTracking: result.data.updateSessionSettings.enableGeoTracking,
        });
      }
      
      // Refresh the context settings so they're available throughout the app
      await refreshSettings();
      
      console.log('[SESSION SETTINGS] Settings saved successfully');
      showNotification('success', 'Settings Saved', 'Session settings saved successfully!');
      
    } catch (error: any) {
      console.error('[SESSION SETTINGS] Failed to save session settings:', error);
      console.error('[SESSION SETTINGS] Error details:', {
        graphQLErrors: error?.graphQLErrors,
        networkError: error?.networkError,
        message: error?.message,
        stack: error?.stack
      });
      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.networkError?.message || error?.message || 'Unknown error occurred';
      showNotification('error', 'Save Failed', `Failed to save session settings: ${errorMessage}`);
    } finally {
      setIsSessionSettingsSaving(false);
    }
  };

  const handleResetSessionSettings = async () => {
    setResetSessionConfirm(true);
  };

  const confirmResetSessionSettings = async () => {
    try {
        await resetSessionSettings();
        
        // Refresh the context settings so they're available throughout the app
        await refreshSettings();
        setResetSessionConfirm(false);
        showNotification('success', 'Settings Reset', 'Session settings have been reset to defaults.');
      } catch (error) {
        console.error('Failed to reset session settings:', error);
        showNotification('error', 'Reset Failed', 'Failed to reset session settings. Please try again.');
      }
  };

  // Geolocation Settings Handlers
  const handleGeolocationSettingsChange = (field: string, value: any) => {
    setGeolocationSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveGeolocationSettings = async () => {
    setIsGeolocationSettingsSaving(true);
    try {
      const sanitizedInput = {
        enabled: Boolean(geolocationSettings.enabled),
        mode: geolocationSettings.mode || 'ALLOW',
        countries: Array.isArray(geolocationSettings.countries) ? geolocationSettings.countries : [],
      };
      
      console.log('[ADMIN DASHBOARD] Saving geolocation settings:', sanitizedInput);
      
      const result = await updateGeolocationSettings({
        variables: { input: sanitizedInput },
        errorPolicy: 'all'
      });
      
      console.log('[ADMIN DASHBOARD] Save result:', result);
      
      if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
        throw new Error(result.errors[0]?.message || 'GraphQL error occurred');
      }
      
      if (!result.data?.updateGeolocationSettings) {
        throw new Error('No data returned from mutation');
      }
      
      if (result.data.updateGeolocationSettings) {
        setGeolocationSettings({
          enabled: result.data.updateGeolocationSettings.enabled,
          mode: result.data.updateGeolocationSettings.mode,
          countries: result.data.updateGeolocationSettings.countries,
          availableCountries: result.data.updateGeolocationSettings.availableCountries || geolocationSettings.availableCountries,
        });
      }
      
      await refreshSettings();
      showNotification('success', 'Settings Saved', 'Geolocation settings saved successfully!');
        
    } catch (error: any) {
      console.error('Failed to save geolocation settings:', error);
      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.networkError?.message || error?.message || 'Unknown error occurred';
      showNotification('error', 'Save Failed', `Failed to save geolocation settings: ${errorMessage}`);
    } finally {
      setIsGeolocationSettingsSaving(false);
      }
  };

  // Notes Settings Handlers
  const handleNotesChange = (enabled: boolean) => {
    setGeneralSettings(prev => ({ ...prev, notesEnabled: enabled }));
  };

  const handleSaveNotesSettings = async () => {
    setIsNotesSettingsSaving(true);
    try {
      await updateApplicationSettings({
        variables: { 
          input: {
            ...generalSettings,
            bugReportEmail: generalSettings.bugReportEmail || generalSettings.supportEmail || 'support@healthcaresolutions.com'
          }
        }
      });
      
      console.log('Notes settings saved successfully');
      showNotification('success', 'Settings Saved', 'Notes settings saved successfully!');
      
    } catch (error) {
      console.error('Failed to save notes settings:', error);
      showNotification('error', 'Save Failed', 'Failed to save notes settings. Please try again.');
    } finally {
      setIsNotesSettingsSaving(false);
    }
  };

  const handleResetNotesSettings = () => {
    setGeneralSettings(prev => ({ ...prev, notesEnabled: true }));
    showNotification('success', 'Settings Reset', 'Notes settings reset to defaults!');
  };

  // Bulletins Settings Handlers
  const handleBulletinsChange = (enabled: boolean) => {
    setGeneralSettings(prev => ({ ...prev, bulletinsEnabled: enabled }));
  };

  const handleSaveBulletinsSettings = async () => {
    setIsBulletinsSettingsSaving(true);
    try {
      await updateApplicationSettings({
        variables: { 
          input: {
            ...generalSettings,
            bugReportEmail: generalSettings.bugReportEmail || generalSettings.supportEmail || 'support@healthcaresolutions.com'
          }
        }
      });
      
      console.log('Bulletins settings saved successfully');
      showNotification('success', 'Settings Saved', 'Bulletins settings saved successfully!');
      
    } catch (error) {
      console.error('Failed to save bulletins settings:', error);
      showNotification('error', 'Save Failed', 'Failed to save bulletins settings. Please try again.');
    } finally {
      setIsBulletinsSettingsSaving(false);
    }
  };

  const handleResetBulletinsSettings = () => {
    setGeneralSettings(prev => ({ ...prev, bulletinsEnabled: true }));
    showNotification('success', 'Settings Reset', 'Bulletins settings reset to defaults!');
  };







  // System Maintenance Handlers
  const handleRestartServer = async () => {
    try {
      setIsRestartModalOpen(true);
      setRestartInitiated(false);
      
      const result = await restartServer();
      
      if (result.data?.restartServer?.success) {
        setRestartInitiated(true);
      } else {

        setIsRestartModalOpen(false);
      }
    } catch (error) {
      console.error('Error restarting server:', error);

      setIsRestartModalOpen(false);
    }
  };

  const handleOptimizeDatabase = () => {
    setIsOptimizeModalOpen(true);
  };

  const handleBackupDatabase = () => {
    setIsBackupModalOpen(true);
  };

  const handleClearCache = () => {
    setIsClearCacheModalOpen(true);
  };

  const handleCSVUpload = (type: 'insurances' | 'locations' | 'conditions' | 'customers' | 'appointment-details' | 'providers') => {
    setCsvImportType(type);
    setIsCSVUploadModalOpen(true);
  };

  const handleCSVFileSelected = (data: string) => {
    setCsvData(data);
    setIsCSVUploadModalOpen(false);
    setIsCSVPreviewModalOpen(true);
  };

  const handleCSVPreviewConfirm = async () => {
    setCsvImportLoading(true);
    try {
      let mutation;
      switch (csvImportType) {
        case 'insurances':
          mutation = IMPORT_INSURANCES;
          break;
        case 'locations':
          mutation = IMPORT_LOCATIONS;
          break;
        case 'conditions':
          mutation = IMPORT_CONDITIONS;
          break;
        case 'customers':
          mutation = IMPORT_CUSTOMERS;
          break;
        case 'appointment-details':
          mutation = IMPORT_APPOINTMENT_DETAILS;
          break;
        case 'providers':
          mutation = IMPORT_PROVIDERS;
          break;
        default:
          throw new Error('Invalid import type');
      }

      const result = await client.mutate({
        mutation,
        variables: { csvData }
      });

      const dataKey = Object.keys(result.data || {})[0];
      const importResult = dataKey ? (result.data as any)[dataKey] : null;

      if (importResult) {
        const summary = `${importResult.importedCount ?? 0} imported, ${importResult.skippedCount ?? 0} skipped, ${importResult.errorCount ?? 0} errors`;
        if (importResult.success) {
          showNotification(importResult.message || summary, 'success');
          await client.refetchQueries({ include: ['GetModeratorData'] });
        } else {
          showNotification(importResult.message || summary, 'error');
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Import failed';
      showNotification(message, 'error');
    } finally {
      setCsvImportLoading(false);
      setIsCSVPreviewModalOpen(false);
      setCsvData('');
    }
  };

  // Get environment name from system info
  const environmentName = systemInfoData?.systemInfo?.application?.environmentName || '';

  // Filter admin sections based on SMTP status and environment
  const allAdminSections = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'general', label: 'General Settings', icon: Settings },
    { id: 'imports', label: 'Imports', icon: Upload },
    { id: 'logs', label: 'Logs', icon: FileText },
    { id: 'email', label: 'Email Settings', icon: Mail },
    { id: 'testing', label: 'System Testing', icon: TestTube },
    { id: 'bug-report', label: 'Bug Report & Feedback', icon: Bug, requiresSmtp: true },
    { id: 'patch-notes', label: 'Patch Notes', icon: BookOpen }
  ];

  // Filter sections - only show bug-report if SMTP is enabled
  const adminSections = allAdminSections.filter(section => {
    if (section.requiresSmtp && !isSmtpEnabled) return false;
    return true;
  });

  // Redirect to overview if user navigates to bug-report when SMTP is disabled
  useEffect(() => {
    if (activeSection === 'bug-report' && !isSmtpEnabled) {
      setActiveSection('overview');
    }
  }, [activeSection, isSmtpEnabled]);

  const renderOverview = () => (
    <div className="space-y-8">
      {/* System Overview */}
      <div 
        className="rounded-xl shadow-sm border p-8"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Administrator Dashboard</h2>
            <p className="text-lg" style={{ color: 'var(--color-text-secondary)' }}>Monitor and manage your system operations.</p>
          </div>
          <div 
            className="p-4 rounded-lg"
            style={{ backgroundColor: 'var(--color-bg-secondary)' }}
          >
            <BarChart3 className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
          </div>
        </div>
      </div>

      {/* Environment & Configuration */}
      <div 
        className="rounded-xl shadow-sm border p-8"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <Server className="w-6 h-6 mr-2" style={{ color: 'var(--color-accent)' }} />
              Environment & Configuration
            </h3>
            <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>Runtime environment and system configuration details</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Environment Variables */}
          <div className="space-y-4">
            <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Environment</h4>
            <div className="space-y-3">
              {systemInfoLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: 'var(--color-accent)' }}></div>
                </div>
              ) : systemInfoError ? (
                <div className="text-center py-4" style={{ color: 'var(--color-error)' }}>
                  Failed to load environment info
                </div>
              ) : systemInfoData?.systemInfo ? (
                [
                  { label: 'Environment Name', value: systemInfoData.systemInfo.application.environmentName },
                  { label: 'NODE_VERSION', value: systemInfoData.systemInfo.environment.nodeVersion },
                  { label: 'PLATFORM', value: systemInfoData.systemInfo.environment.platform },
                  { label: 'PORT', value: systemInfoData.systemInfo.services.find((s: any) => s.name === 'GraphQL Server')?.port || '4000' },
                  {
                    label: 'Build Date',
                    value: systemInfoData.systemInfo.application.buildTime
                      ? formatBuildDate(systemInfoData.systemInfo.application.buildTime)
                      : 'N/A'
                  }
                ].map((env, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 border rounded-lg"
                    style={{ 
                      borderColor: 'var(--color-border)',
                      backgroundColor: 'var(--color-bg-secondary)'
                    }}
                  >
                    <span style={{ color: 'var(--color-text-secondary)' }}>{env.label}</span>
                    <span className="font-mono text-right" style={{ color: 'var(--color-text-primary)' }}>{env.value}</span>
                  </div>
                ))
              ) : (
                <div className="text-center py-4" style={{ color: 'var(--color-text-muted)' }}>
                  No environment data available
                </div>
              )}
            </div>
          </div>

          {/* Runtime Info */}
          <div className="space-y-4">
            <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Runtime</h4>
            <div className="space-y-3">
              {systemInfoLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: 'var(--color-accent)' }}></div>
                </div>
              ) : systemInfoError ? (
                <div className="text-center py-4" style={{ color: 'var(--color-error)' }}>
                  Failed to load runtime info
                </div>
              ) : systemInfoData?.systemInfo ? (
                [
                  { label: 'Node.js Version', value: systemInfoData.systemInfo.environment.nodeVersion },
                  { label: 'Platform', value: systemInfoData.systemInfo.environment.platform },
                  { label: 'Uptime', value: `${Math.floor(systemInfoData.systemInfo.environment.uptime / 3600)}h ${Math.floor((systemInfoData.systemInfo.environment.uptime % 3600) / 60)}m` },
                  { label: 'Process ID', value: systemInfoData.systemInfo.environment.processId.toString() }
                ].map((info, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 border rounded-lg"
                    style={{ 
                      borderColor: 'var(--color-border)',
                      backgroundColor: 'var(--color-bg-secondary)'
                    }}
                  >
                    <span style={{ color: 'var(--color-text-secondary)' }}>{info.label}</span>
                    <span className="font-mono" style={{ color: 'var(--color-text-primary)' }}>{info.value}</span>
                  </div>
                ))
              ) : (
                <div className="text-center py-4" style={{ color: 'var(--color-text-muted)' }}>
                  No runtime data available
                </div>
              )}
            </div>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Services</h4>
            <div className="space-y-3">
              {systemInfoLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: 'var(--color-accent)' }}></div>
                </div>
              ) : systemInfoError ? (
                <div className="text-center py-4" style={{ color: 'var(--color-error)' }}>
                  Failed to load services info
                </div>
              ) : systemInfoData?.systemInfo ? (
                systemInfoData.systemInfo.services.map((service: any, index: number) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 border rounded-lg"
                    style={{ 
                      borderColor: 'var(--color-border)',
                      backgroundColor: 'var(--color-bg-secondary)'
                    }}
                  >
                    <span style={{ color: 'var(--color-text-secondary)' }}>{service.name}</span>
                    <div className="flex items-center space-x-2">
                      <span 
                        className="px-2 py-1 rounded-full text-xs"
                        style={{
                          backgroundColor: service.status === 'Running' 
                            ? 'var(--color-success)' 
                            : 'var(--color-bg-primary)',
                          color: service.status === 'Running' ? 'white' : 'var(--color-text-muted)'
                        }}
                      >
                        {service.status}
                      </span>
                      {service.port && (
                        <span className="font-mono text-sm" style={{ color: 'var(--color-text-secondary)' }}>:{service.port}</span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4" style={{ color: 'var(--color-text-muted)' }}>
                  No services data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* System Health */}
      <div 
        className="rounded-xl shadow-sm border p-8"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <CheckCircle className="w-6 h-6 mr-2" style={{ color: 'var(--color-accent)' }} />
              System Health
            </h3>
            <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              Real-time system resource monitoring
              {systemHealthData?.systemHealth.timestamp && (
                <span className="ml-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  • Updated {formatTimeInTimezone(systemHealthData.systemHealth.timestamp, settings?.defaultTimezone || 'America/New_York')}
                </span>
              )}
            </p>
          </div>
        </div>

        {systemHealthLoading ? (
          <div className="flex items-center justify-center py-8">
            <div 
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: 'var(--color-accent)' }}
            ></div>
            <span className="ml-3" style={{ color: 'var(--color-text-secondary)' }}>Loading system health...</span>
          </div>
        ) : systemHealthError ? (
          <div 
            className="p-4 border rounded-lg"
            style={{ 
              borderColor: 'var(--color-error)',
              backgroundColor: 'var(--color-bg-error)'
            }}
          >
            <div className="text-center" style={{ color: 'var(--color-error)' }}>
              Failed to load system health data. Please check server connection.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {formatSystemHealthData(systemHealthData?.systemHealth).map((metric) => (
              <div 
                key={metric.label} 
                className="p-4 border rounded-lg"
                style={{ 
                  borderColor: 'var(--color-border)',
                  backgroundColor: 'var(--color-bg-secondary)'
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{metric.label}</span>
                  <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{metric.value}</span>
                </div>
                <div 
                  className="w-full rounded-full h-2 mb-2"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                >
                  <div 
                    className="h-2 rounded-full" 
                    style={{ 
                      width: `${metric.percentage}%`, 
                      backgroundColor: metric.color
                    }}
                  ></div>
                </div>
                <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{metric.details}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* System Statistics */}
      <div 
        className="rounded-xl shadow-sm border p-8"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <BarChart3 className="w-6 h-6 mr-2" style={{ color: 'var(--color-accent)' }} />
              System Statistics
            </h3>
            <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>Real-time system usage and performance metrics</p>
          </div>
        </div>

        {systemStatsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div 
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: 'var(--color-accent)' }}
            ></div>
            <span className="ml-3" style={{ color: 'var(--color-text-secondary)' }}>Loading system statistics...</span>
          </div>
        ) : systemStatsError ? (
          <div 
            className="p-4 border rounded-lg"
            style={{ 
              borderColor: 'var(--color-error)',
              backgroundColor: 'var(--color-bg-error)'
            }}
          >
            <div className="text-center" style={{ color: 'var(--color-error)' }}>
              Failed to load system statistics. Please check server connection.
            </div>
          </div>
        ) : systemStatsData?.systemStats ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* User Statistics */}
            <div className="p-4 border rounded-lg" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}>
              <div className="flex items-center mb-3">
                <Users className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
                <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Users</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Total:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{systemStatsData.systemStats.users.total}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Active:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{systemStatsData.systemStats.users.active}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Admins:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{systemStatsData.systemStats.users.administrators}</span>
                </div>
              </div>
            </div>

            {/* Session Statistics */}
            <div className="p-4 border rounded-lg" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}>
              <div className="flex items-center mb-3">
                <Shield className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
                <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Sessions</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Active:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{systemStatsData.systemStats.sessions.totalActive}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Today:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{systemStatsData.systemStats.sessions.todayCreated}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Avg Duration:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{Math.round(systemStatsData.systemStats.sessions.averageDuration)}m</span>
                </div>
              </div>
            </div>

            {/* Log Statistics */}
            <div className="p-4 border rounded-lg" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}>
              <div className="flex items-center mb-3">
                <FileText className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
                <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Logs</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Total:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{systemStatsData.systemStats.logs.total}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Errors:</span>
                  <span style={{ color: 'var(--color-error)' }}>{systemStatsData.systemStats.logs.errors}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Last 24h:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{systemStatsData.systemStats.logs.last24Hours}</span>
                </div>
              </div>
            </div>

            {/* Database Statistics */}
            <div className="p-4 border rounded-lg" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}>
              <div className="flex items-center mb-3">
                <Server className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
                <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Database</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Records:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{systemStatsData.systemStats.database.totalRecords.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Tables:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{systemStatsData.systemStats.database.tables}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Size:</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>{(systemStatsData.systemStats.database.size / 1024 / 1024).toFixed(1)} MB</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
            No system statistics available
          </div>
        )}
      </div>

      {/* System Maintenance */}
      <div 
        className="rounded-xl shadow-sm border p-8"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <Cog className="w-6 h-6 mr-2" style={{ color: 'var(--color-accent)' }} />
              System Maintenance
            </h3>
            <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>Essential system maintenance operations</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { 
              action: 'Restart Server', 
              description: 'Gracefully restart the application server', 
              icon: RefreshCw, 
              handler: handleRestartServer,
              color: 'var(--color-accent)'
            },
            { 
              action: 'Optimize Database', 
              description: 'Analyze and optimize database performance', 
              icon: Zap, 
              handler: handleOptimizeDatabase,
              color: 'var(--color-warning)'
            },
            { 
              action: 'Backup Database', 
              description: 'Create a full backup of the current database', 
              icon: Database, 
              handler: handleBackupDatabase,
              color: 'var(--color-info)'
            },
            { 
              action: 'Clear Cache', 
              description: 'Clear application and database cache', 
              icon: Trash2, 
              handler: handleClearCache,
              color: 'var(--color-error)'
            }
          ].map((item, index) => {
            const IconComponent = item.icon;
            return (
              <button 
                key={index} 
                onClick={item.handler}
                className="text-left p-4 border rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105"
                style={{ 
                  borderColor: 'var(--color-border)',
                  backgroundColor: 'var(--color-bg-secondary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                  e.currentTarget.style.borderColor = item.color;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                }}
              >
                <div className="w-12 h-12 mb-3 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${item.color}20` }}>
                  <IconComponent className="w-6 h-6" style={{ color: item.color }} />
                </div>
                <div className="font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>{item.action}</div>
                <div className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>{item.description}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderUserManagement = () => <UserManagement />;

  const renderImports = () => (
    <div className="space-y-8">
      {/* Imports Header */}
      <div 
        className="rounded-xl shadow-sm border p-8"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <Upload className="w-6 h-6 mr-2" style={{ color: 'var(--color-accent)' }} />
              CSV Imports
            </h3>
            <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>Import data from CSV files for various system components</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b mb-6" style={{ borderColor: 'var(--color-border)' }}>
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'insurances', label: 'Insurances', icon: Shield, shortcut: '1' },
              { id: 'locations', label: 'Locations', icon: Server, shortcut: '2' },
              { id: 'conditions', label: 'Conditions', icon: FileText, shortcut: '3' },
              { id: 'customers', label: 'Customers', icon: Building, shortcut: '4' },
              { id: 'appointments', label: 'Appointment Details', icon: CheckCircle, shortcut: '5' },
              { id: 'providers', label: 'Providers', icon: Users, shortcut: '6' }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeImportTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveImportTab(tab.id as any)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    isActive
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  style={{
                    borderColor: isActive ? 'var(--color-accent)' : 'transparent',
                    color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)'
                  }}
                >
                  <Icon className="w-4 h-4 inline mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeImportTab === 'insurances' && (
          <div 
            className="rounded-xl shadow-sm border p-8 mt-8"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <h4 className="text-lg font-semibold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <Shield className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
              Insurance Import
            </h4>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              Import insurance from CSV files.
            </p>

            <div className="text-center">
              <button 
                onClick={() => handleCSVUpload('insurances')}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2 mx-auto"
              >
                <Upload className="w-5 h-5" />
                <span>Import</span>
              </button>
            </div>
          </div>
        )}

        {activeImportTab === 'locations' && (
          <div 
            className="rounded-xl shadow-sm border p-8 mt-8"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <h4 className="text-lg font-semibold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <Server className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
              Location Import
            </h4>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              Import locations from CSV files.
            </p>

            <div className="text-center">
              <button 
                onClick={() => handleCSVUpload('locations')}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2 mx-auto"
              >
                <Upload className="w-5 h-5" />
                <span>Import</span>
              </button>
            </div>
          </div>
        )}

        {activeImportTab === 'conditions' && (
          <div 
            className="rounded-xl shadow-sm border p-8 mt-8"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <h4 className="text-lg font-semibold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <FileText className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
              Condition Import
            </h4>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              Import medical conditions from CSV files.
            </p>
            <div className="text-center">
              <button 
                onClick={() => handleCSVUpload('conditions')}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2 mx-auto"
              >
                <Upload className="w-5 h-5" />
                <span>Import</span>
              </button>
            </div>
          </div>
        )}

        {activeImportTab === 'customers' && (
          <div 
            className="rounded-xl shadow-sm border p-8 mt-8"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <h4 className="text-lg font-semibold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <Building className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
              Customer Import
            </h4>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              Import customers from CSV files.
            </p>
            <div className="text-center">
              <button 
                onClick={() => handleCSVUpload('customers')}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2 mx-auto"
              >
                <Upload className="w-5 h-5" />
                <span>Import</span>
              </button>
            </div>
          </div>
        )}

        {activeImportTab === 'appointments' && (
          <div 
            className="rounded-xl shadow-sm border p-8 mt-8"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <h4 className="text-lg font-semibold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <CheckCircle className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
              Appointment Details Import
            </h4>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              Import appointment detail data from CSV files. Can Cancel and Can Reschedule accept: "Yes", "No", or "See Notes". Legacy boolean values ("true"/"false") are also accepted and will be converted automatically.
            </p>
            <div className="text-center">
              <button 
                onClick={() => handleCSVUpload('appointment-details')}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2 mx-auto"
              >
                <Upload className="w-5 h-5" />
                <span>Import</span>
              </button>
            </div>
          </div>
        )}

        {activeImportTab === 'providers' && (
          <div 
            className="rounded-xl shadow-sm border p-8 mt-8"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <h4 className="text-lg font-semibold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <Users className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
              Provider Import
            </h4>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              Import provider data from CSV files. Ensure your CSV has the required columns: name, license, contact info, etc.
            </p>
            <div className="text-center">
              <button 
                onClick={() => handleCSVUpload('providers')}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2 mx-auto"
              >
                <Upload className="w-5 h-5" />
                <span>Import</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );



  const renderGeneralSettings = () => (
    <div className="space-y-8">
      {/* General Settings Header */}
      <div 
        className="rounded-xl shadow-sm border p-8"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <Settings className="w-6 h-6 mr-2" style={{ color: 'var(--color-accent)' }} />
              General Settings
            </h3>
            <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>Configure general application preferences and behavior</p>
          </div>
          
        </div>

        {/* Tabs */}
        <div className="border-b mb-6" style={{ borderColor: 'var(--color-border)' }}>
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'application', label: 'Application', icon: Settings, shortcut: '1' },
              { id: 'notes', label: 'Notes', icon: FileText, shortcut: '2' },
              { id: 'bulletins', label: 'Bulletins', icon: Bell, shortcut: '3' },
              { id: 'sessions', label: 'Sessions', icon: Shield, shortcut: '4' },
              { id: 'geolocation', label: 'Geolocation', icon: Globe, shortcut: '5' }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeGeneralTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveGeneralTab(tab.id as any)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    isActive
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  style={{
                    borderColor: isActive ? 'var(--color-accent)' : 'transparent',
                    color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)'
                  }}
                >
                  <Icon className="w-4 h-4 inline mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>

        </div>



        {/* Tab Content */}
        {activeGeneralTab === 'application' && (
          <ApplicationSettings
            generalSettings={generalSettings}
            isGeneralSettingsSaving={isGeneralSettingsSaving}
            onSettingsChange={handleGeneralSettingsChange}
            onSave={handleSaveGeneralSettings}
            onReset={handleResetGeneralSettings}
          />
        )}
          </div>
          
      {/* Notes Tab Content */}
      {activeGeneralTab === 'notes' && (
        <div 
          className="rounded-xl shadow-sm border p-8 mt-8"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
                    <NotesSettings
            generalSettings={generalSettings}
            isNotesSettingsSaving={isNotesSettingsSaving}
            onNotesChange={handleNotesChange}
            onSave={handleSaveNotesSettings}
            onReset={handleResetNotesSettings}
          />
          </div>
      )}

      {/* Bulletins Tab Content */}
      {activeGeneralTab === 'bulletins' && (
        <div 
          className="rounded-xl shadow-sm border p-8 mt-8"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
                    <BulletinsSettings
            generalSettings={generalSettings}
            isBulletinsSettingsSaving={isBulletinsSettingsSaving}
            onBulletinsChange={handleBulletinsChange}
            onSave={handleSaveBulletinsSettings}
            onReset={handleResetBulletinsSettings}
          />
          </div>
      )}

      {/* Sessions Tab Content */}
      {activeGeneralTab === 'sessions' && (
        <div 
          className="rounded-xl shadow-sm border p-8 mt-8"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <SessionsSettings
            sessionSettings={sessionSettings}
            isSessionSettingsSaving={isSessionSettingsSaving}
            onSessionSettingsChange={handleSessionSettingsChange}
            onSave={() => {
              console.log('[ADMIN DASHBOARD] onSave callback called from SessionsSettings');
              handleSaveSessionSettings();
            }}
            onReset={handleResetSessionSettings}
          />
        </div>
      )}

      {/* Geolocation Tab Content */}
      {activeGeneralTab === 'geolocation' && (
        <div 
          className="rounded-xl shadow-sm border p-8 mt-8"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <GeolocationSettings
            geolocationSettings={geolocationSettings}
            isGeolocationSettingsSaving={isGeolocationSettingsSaving}
            onGeolocationSettingsChange={handleGeolocationSettingsChange}
            onSave={handleSaveGeolocationSettings}
          />
        </div>
      )}

      {/* Feature Toggle Confirmation Modal */}
      {showFeatureToggleConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div 
            className="rounded-lg shadow-xl max-w-md w-full"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
              boxShadow: 'var(--shadow-xl)'
            }}
          >
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="w-6 h-6" style={{ color: 'var(--color-warning)' }} />
                <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Confirm Feature Toggle
                </h3>
              </div>
              
              <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                Are you sure you want to <strong>{pendingFeatureToggle?.enabled ? 'enable' : 'disable'}</strong> the{' '}
                <strong>{pendingFeatureToggle?.feature}</strong> feature?
              </p>
              
              <div className="p-4 border rounded-lg mb-6" style={{ 
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border)'
            }}>
                <h6 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>Impact:</h6>
                <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {pendingFeatureToggle?.enabled 
                    ? `Enabling ${pendingFeatureToggle?.feature} will make the feature available to users and show all related UI elements.`
                    : `Disabling ${pendingFeatureToggle?.feature} will hide the feature from users and remove all related UI elements.`
                  }
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="secondary"
                  onClick={cancelFeatureToggle}
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={confirmFeatureToggle}
                  size="sm"
                >
                  {pendingFeatureToggle?.enabled ? 'Enable' : 'Disable'} Feature
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );



  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return renderOverview();
      case 'users':
        return renderUserManagement();
      case 'general':
        return renderGeneralSettings();
      case 'imports':
        return renderImports();
      case 'logs':
        return <LogsManagement activeLogTab={activeLogTab} setActiveLogTab={setActiveLogTab} />;
      case 'email':
        return <EmailConfigurationDashboard />;
      case 'testing':
        return <TestingDashboard />;
      case 'bug-report':
        return <BugReportFeedback />;
      case 'patch-notes':
        return <PatchNotesPage />;
      default:
        return renderOverview();
    }
  };

  return (
    <div className="space-y-8 w-full max-w-full">
        <div className="flex flex-col lg:flex-row lg:space-x-8 w-full">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 lg:min-w-64 lg:flex-shrink-0 mb-8 lg:mb-0">
            <nav className="space-y-2">
              {adminSections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                
                return (
                  <Button
                    key={section.id}
                    variant={isActive ? "primary" : "ghost"}
                    onClick={() => {
                        setActiveSection(section.id);
                    }}
                    className="w-full justify-start whitespace-nowrap"
                    leftIcon={<Icon className="w-5 h-5" />}
                  >
                    {section.label}
                  </Button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div 
            className="flex-1 min-w-0 w-full overflow-y-auto flex flex-col admin-dashboard-scrollbar" 
            style={{ height: 'calc(100vh - 200px)' }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSection}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="w-full flex flex-col"
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      
      {/* Server Restart Modal */}
      <ServerRestartModal
        isOpen={isRestartModalOpen}
        onClose={() => {
          setIsRestartModalOpen(false);
          setRestartInitiated(false);
        }}
        restartInitiated={restartInitiated}
      />

      {/* Database Backup Modal */}
      <DatabaseBackupModal
        isOpen={isBackupModalOpen}
        onClose={() => setIsBackupModalOpen(false)}
      />

      {/* Database Optimize Modal */}
      <DatabaseOptimizeModal
        isOpen={isOptimizeModalOpen}
        onClose={() => setIsOptimizeModalOpen(false)}
      />

      {/* Clear Cache Modal */}
      <ClearCacheModal
        isOpen={isClearCacheModalOpen}
        onClose={() => setIsClearCacheModalOpen(false)}
      />

              {/* CSV Upload Modal */}
              <CSVUploadModal
                isOpen={isCSVUploadModalOpen}
                onClose={() => setIsCSVUploadModalOpen(false)}
                importType={csvImportType}
                onFileSelected={handleCSVFileSelected}
              />

              {/* CSV Preview Modal */}
              <CSVPreviewModal
                isOpen={isCSVPreviewModalOpen}
                onClose={() => { if (!csvImportLoading) { setIsCSVPreviewModalOpen(false); setCsvData(''); } }}
                csvData={csvData}
                importType={csvImportType}
                onConfirm={handleCSVPreviewConfirm}
                isImporting={csvImportLoading}
              />

      {/* Feature Toggle Confirmation Modal */}
      {showFeatureToggleConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div 
            className="rounded-lg shadow-xl max-w-md w-full"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              boxShadow: 'var(--shadow-xl)'
            }}
          >
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="w-6 h-6" style={{ color: 'var(--color-warning)' }} />
                <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Confirm Feature Toggle
                </h3>
              </div>
              
              <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                Are you sure you want to <strong>{pendingFeatureToggle?.enabled ? 'enable' : 'disable'}</strong> the{' '}
                <strong>{pendingFeatureToggle?.feature}</strong> feature?
              </p>
              
              <div className="p-4 border rounded-lg mb-6" style={{ 
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)'
              }}>
                <h6 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>Impact:</h6>
                <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {pendingFeatureToggle?.enabled 
                    ? `Enabling ${pendingFeatureToggle?.feature} will make the feature available to users and show all related UI elements.`
                    : `Disabling ${pendingFeatureToggle?.feature} will hide the feature from users and remove all related UI elements.`
                  }
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="secondary"
                  onClick={cancelFeatureToggle}
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={confirmFeatureToggle}
                  size="sm"
                >
                  {pendingFeatureToggle?.enabled ? 'Enable' : 'Disable'} Feature
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Application Settings Modal */}
      <ApplicationSettingsModal
        isOpen={isAppSettingsModalOpen}
        onClose={() => setIsAppSettingsModalOpen(false)}
      />

      {/* Reset Confirmation Modals */}
      <ConfirmModal
        isOpen={resetGeneralConfirm}
        onClose={() => setResetGeneralConfirm(false)}
        onConfirm={confirmResetGeneralSettings}
        title="Reset General Settings"
        message="Are you sure you want to reset all general settings to defaults? This will overwrite your current settings."
        type="warning"
        confirmLabel="Reset"
        cancelLabel="Cancel"
      />

      <ConfirmModal
        isOpen={resetSessionConfirm}
        onClose={() => setResetSessionConfirm(false)}
        onConfirm={confirmResetSessionSettings}
        title="Reset Session Settings"
        message="Are you sure you want to reset session settings to defaults? This will overwrite your current settings."
        type="warning"
        confirmLabel="Reset"
        cancelLabel="Cancel"
      />

      {/* Notification Component */}
      <Notification
        type={notification.type}
        title={notification.title}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
        duration={5000}
      />
    </div>
  );
};
